import React from "react";
import { motion } from "framer-motion";
import { fadeUp } from "../../lib/motion";

const packages = [
  {
    id: "essential",
    name: "The Essential Edit",
    price: "£1,200",
    tagline: "A clear, structured foundation for your business—so you can stop guessing and start operating properly.",
    support: "You'll have the essential structure in place to work with clients confidently and professionally.",
    items: [
      "Contracts and policies",
      "Client intake form",
      "Email templates",
      "SOPs",
      "File organisation",
      "Client tracker",
      "Website policy guidance",
      "Consultation call",
      "Handover pack",
    ],
  },
  {
    id: "signature",
    name: "The Signature Edit",
    price: "£1,500",
    tagline: "A fully structured and refined backend—so your business runs smoothly, professionally, and without constant second-guessing.",
    support: "This is where your business shifts from feeling pieced together to fully established.",
    featured: true,
    items: [
      "Everything in Essential",
      "Client welcome pack",
      "Invoice template",
      "Advanced client tracker",
      "Onboarding checklist",
      "Testimonial template",
      "Re-engagement email",
      "Tools & systems guide",
    ],
  },
  {
    id: "luxe",
    name: "The Luxe Edit",
    price: "£1,850",
    tagline: "Your business, fully refined and elevated—delivered as a complete, premium experience.",
    support: "Everything is structured, organised, and presented in a way that makes your business feel real, established, and ready to grow.",
    items: [
      "Everything in Signature",
      "Printed business setup pack",
      "Premium folder / binder",
      "Branded notepad",
      "Branded pen set (premium finish)",
      "Business cards",
      "Luxe packaging",
      "Personalised welcome note",
      "Optional 1:1 walkthrough call",
    ],
  },
];

export default function Packages() {
  const selectAndScroll = (id) => {
    const input = document.querySelector('[data-testid="enquiry-package-select"]');
    if (input) input.value = id;
    document.getElementById("enquire")?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <section id="packages" data-testid="packages-section" className="py-24 sm:py-32 bg-[color:var(--bg-alt)]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <motion.div {...fadeUp} className="max-w-3xl">
          <p className="overline mb-6">— Packages</p>
          <h2 className="font-serif-display font-medium text-4xl sm:text-5xl tracking-tight text-[color:var(--charcoal)] leading-[1.08]">
            Choose
            <span className="italic text-[color:var(--gold)]"> your Edit</span>.
          </h2>
          <p className="mt-6 text-lg text-[color:var(--text-secondary)] font-light">
            Each tier delivers a full, done-for-you foundation — scaled to where your business is.
          </p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="packages-grid">
          {packages.map((p, idx) => (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8, delay: idx * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className={`package-card ${p.featured ? "featured md:-my-6 md:py-16" : ""}`}
              data-testid={`package-card-${p.id}`}
            >
              {p.featured && <span className="featured-badge" data-testid="featured-badge">Most Popular</span>}
              <p className="overline mb-4" style={p.featured ? { color: "var(--gold)" } : {}}>
                {p.featured ? "— Signature" : p.id === "essential" ? "— Foundation" : "— Complete"}
              </p>
              <h3 className="pkg-title font-serif-display text-3xl text-[color:var(--charcoal)] leading-tight">{p.name}</h3>
              <p className="price mt-6 font-serif-display text-4xl font-light text-[color:var(--charcoal)]">{p.price}</p>
              <p className="pkg-desc mt-4 text-sm text-[color:var(--text-secondary)] leading-relaxed italic">{p.tagline}</p>
              {p.support && (
                <p className="pkg-desc mt-4 text-sm text-[color:var(--text-secondary)] leading-relaxed">{p.support}</p>
              )}
              <div className="mt-8 h-px w-12" style={{ background: p.featured ? "var(--gold)" : "var(--border-medium)" }} />
              <ul className={`check-list mt-8 flex-1 ${p.featured ? "space-y-3" : "space-y-1"}`}>
                {p.items.map((it) => (
                  <li key={it}>{it}</li>
                ))}
              </ul>
              <button
                onClick={() => selectAndScroll(p.id)}
                data-testid={`package-cta-${p.id}`}
                className={p.featured ? "btn-ghost-light mt-10" : "btn-outline mt-10"}
                style={{ alignSelf: "flex-start" }}
              >
                Apply to Work Together
              </button>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
